import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Presentation, ChevronLeft, ChevronRight, Play, Pause,
  Download, Printer, ArrowRight, Brain, Target, Shield,
  TrendingUp, Users, AlertTriangle, Lightbulb, Sparkles,
  BarChart3, Eye, Zap, CheckCircle2, Quote, Volume2, VolumeX,
  MessageSquare, ChevronDown, ChevronUp, Hand, DollarSign,
  Box, Lock, Building2, LineChart, PieChart, Gauge
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, prefix = "", suffix = "", className = "" }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}{count}{suffix}
    </span>
  );
};

// Interactive Reveal Component
const RevealList = ({ items, className = "" }) => {
  const [revealed, setRevealed] = useState([]);

  const revealNext = () => {
    if (revealed.length < items.length) {
      setRevealed([...revealed, revealed.length]);
    }
  };

  const revealAll = () => {
    setRevealed(items.map((_, i) => i));
  };

  return (
    <div className={className}>
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div 
            key={idx} 
            className={`transform transition-all duration-500 ${
              revealed.includes(idx) 
                ? 'opacity-100 translate-x-0' 
                : 'opacity-0 -translate-x-8'
            }`}
          >
            {item}
          </div>
        ))}
      </div>
      {revealed.length < items.length && (
        <div className="mt-4 flex gap-2">
          <Button 
            onClick={revealNext} 
            size="sm" 
            className="bg-[#2563EB]/20 hover:bg-[#2563EB]/30 text-[#2563EB]"
          >
            Reveal Next
          </Button>
          <Button 
            onClick={revealAll} 
            size="sm" 
            variant="ghost"
            className="text-[#64748B] hover:text-[#94A3B8]"
          >
            Show All
          </Button>
        </div>
      )}
    </div>
  );
};

const TEDTalkPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const sectionColors = {
    opening: "from-[#0A0F1C] via-[#111827] to-[#0A0F1C]",
    problem: "from-[#1a0a0a] via-[#111827] to-[#0A0F1C]",
    aitraps: "from-[#0a1a1a] via-[#111827] to-[#0A0F1C]",
    reframe: "from-[#0a0a1a] via-[#111827] to-[#0A0F1C]",
    solution: "from-[#0a1a0f] via-[#111827] to-[#0A0F1C]",
    impact: "from-[#1a0a1a] via-[#111827] to-[#0A0F1C]",
    closing: "from-[#0A0F1C] via-[#1a1a2a] to-[#0A0F1C]"
  };

  const slides = [
    {
      id: 0,
      type: "title",
      section: "opening",
      title: "Why Most Businesses Are Still Guessing Their Prices",
      subtitle: "— and How AI Changes That",
      speaker: "AxioSera",
      note: "A perspective on decision science, AI, and the future of pricing",
      speakerNotes: "Welcome everyone. Today I want to challenge something we all take for granted - how we set prices. This talk will change how you think about one of the most powerful levers in your business.",
      visual: "🎯"
    },
    {
      id: 1,
      type: "audience",
      section: "opening",
      title: "A Simple Question",
      prompts: [
        { text: "Raise your hand if you believe your company is good at pricing", icon: Hand, pause: "Wait for hands..." },
        { text: "Now — keep it raised if you believe you're scientific about pricing", icon: Hand, pause: "Watch hands drop..." }
      ],
      observation: "Usually, the second group is much smaller.",
      speakerNotes: "This is your opening engagement. Make eye contact. Count the hands. The contrast between the two questions is your hook. Let the silence after the second question land."
    },
    {
      id: 2,
      type: "stat-animated",
      section: "opening",
      title: "The Power of Pricing",
      stats: [
        { value: 1, label: "improvement in price", color: "text-[#2563EB]", suffix: "%" },
        { value: 10, label: "improvement in cost", color: "text-[#64748B]", suffix: "%" }
      ],
      message: "often delivers MORE profit impact than",
      speakerNotes: "Pause here. Let this sink in. This statistic comes from McKinsey research and has been validated across industries. A 1% price increase flows directly to profit. A 10% cost reduction is hard won and often temporary.",
      example: "For a company with $10M revenue and 10% margins, a 1% price increase = $100K profit. To get the same from cost cuts, you'd need to eliminate $1M in costs."
    },
    {
      id: 3,
      type: "visual-list",
      section: "opening",
      title: "Yet Most Businesses Still Price Like It's 2005",
      items: [
        { text: "Spreadsheets", icon: "📊", visual: "Complex Excel sheets passed around" },
        { text: "Gut feel", icon: "🎲", visual: "\"I think we can charge more...\"" },
        { text: "Annual reviews", icon: "📅", visual: "Once a year, if we remember" },
        { text: "A lot of hope", icon: "🤞", visual: "\"Hopefully competitors don't notice\"" }
      ],
      contrast: "In an age where AI can generate images, write code, and predict proteins — we are still guessing our prices.",
      speakerNotes: "Move through these quickly. The humor is in the recognition. Your audience will be nodding. Then hit them with the contrast - make them feel the absurdity."
    },
    {
      id: 4,
      type: "problem-visual",
      section: "problem",
      title: "Most Organisations Believe Pricing Is 'Solved'",
      tools: [
        { name: "ERP Systems", icon: Box },
        { name: "BI Dashboards", icon: PieChart },
        { name: "Sales Analytics", icon: LineChart },
        { name: "Forecasting Tools", icon: TrendingUp }
      ],
      truth: {
        myth: "These tools have pricing covered",
        reality: "They explain the past. Pricing decisions live in the future."
      },
      speakerNotes: "This is where you challenge their assumptions. Many in the audience have invested heavily in these tools. Acknowledge the value, but pivot to the gap.",
      visual: "timeline"
    },
    {
      id: 5,
      type: "questions-reveal",
      section: "problem",
      title: "Every Pricing Decision Is Actually a Prediction",
      questions: [
        "What will customers do if we change this price?",
        "How sensitive is demand?",
        "Where is the real profit cliff?",
        "What's the risk of doing nothing?"
      ],
      conclusion: "Businesses have had no safe way to answer these questions before making the decision.",
      speakerNotes: "Reveal these one by one. Let each question land. These are the questions that keep pricing managers up at night. The audience should feel seen."
    },
    {
      id: 6,
      type: "metaphor",
      section: "problem",
      title: "The Real Cost of Guessing",
      visual: "experiment",
      content: {
        what: "So they experiment…",
        with: ["Real customers", "Real revenue", "Real consequences"],
        verdict: "That's not strategy. That's exposure."
      },
      example: {
        company: "A mid-size retailer",
        story: "Tested a 15% price increase on a key category. Lost 40% of volume. Took 8 months to recover customer trust.",
        lesson: "The 'experiment' cost them $2.3M in lost revenue."
      },
      speakerNotes: "This is your emotional peak of the problem section. The example makes it real. Pause before 'That's exposure' - it should land like a verdict."
    },
    {
      id: 7,
      type: "section-bridge",
      section: "aitraps",
      title: "Surely AI Has Solved This By Now?",
      answer: "Not really.",
      visual: "🤖❌",
      speakerNotes: "Quick transition slide. Say the question, pause, then deliver the answer with slight disappointment. You're acknowledging the elephant in the room."
    },
    {
      id: 8,
      type: "traps-visual",
      section: "aitraps",
      title: "Three Traps of AI Pricing Tools",
      traps: [
        {
          number: "1",
          title: "They optimise locally, not economically",
          description: "They tweak prices without understanding margin, risk, or long-term impact.",
          visual: "🔍",
          metaphor: "Like adjusting the thermostat without knowing the building is on fire"
        },
        {
          number: "2", 
          title: "They are black boxes",
          description: "They give you a number, but not a reason — which makes leaders understandably nervous.",
          visual: "📦",
          metaphor: "Would you bet your job on a number you can't explain?"
        },
        {
          number: "3",
          title: "They are built for giants",
          description: "Huge enterprises with massive data science teams — not real-world operators.",
          visual: "🏢",
          metaphor: "A Ferrari for someone who needs a reliable pickup truck"
        }
      ],
      speakerNotes: "Take your time here. Each trap deserves its own beat. The metaphors help make abstract problems concrete. Your audience has probably experienced at least one of these."
    },
    {
      id: 9,
      type: "consequence-chain",
      section: "aitraps",
      title: "The Result?",
      chain: [
        { text: "AI adds mystery", icon: Brain },
        { text: "Leaders don't trust it", icon: AlertTriangle },
        { text: "They override recommendations", icon: Users },
        { text: "Back to gut feel", icon: Target }
      ],
      conclusion: "Instead of empowering decision-makers, AI often just adds another layer of mystery.",
      speakerNotes: "Show the chain reaction. This is why AI adoption in pricing has been slow. Trust is the real issue, not technology."
    },
    {
      id: 10,
      type: "reframe-visual",
      section: "reframe",
      title: "Here's the Shift That Changes Everything",
      before: {
        label: "OLD THINKING",
        text: "Pricing is a number problem",
        visual: "🔢",
        color: "red"
      },
      after: {
        label: "NEW THINKING",
        text: "Pricing is a decision problem",
        visual: "🧠",
        color: "green"
      },
      speakerNotes: "This is your pivot moment. The reframe. Say it slowly. This single shift in perspective changes everything about how you approach pricing technology."
    },
    {
      id: 11,
      type: "needs-reveal",
      section: "reframe",
      title: "What Leaders Actually Need",
      subtitle: "Not 'the right price' — but understanding:",
      needs: [
        { text: "The range of safe prices", icon: Shield },
        { text: "The risk boundaries", icon: AlertTriangle },
        { text: "The trade-off between growth and margin", icon: TrendingUp },
        { text: "The economic consequence of every option", icon: DollarSign }
      ],
      conclusion: "Pricing needs to become a decision science, not an optimisation trick.",
      speakerNotes: "Reveal these one by one. Each represents a shift from 'give me THE answer' to 'help me understand my options.' This is what empowerment looks like."
    },
    {
      id: 12,
      type: "vision-visual",
      section: "solution",
      title: "We Started With a Simple Question",
      question: "What if businesses could see the future impact of a pricing decision before making it?",
      clarifications: [
        { text: "Not a guess", visual: "❌🎲" },
        { text: "Not a recommendation", visual: "❌📋" },
        { text: "But a simulation", visual: "✅🔮" }
      ],
      speakerNotes: "Let the question breathe. This is your 'imagine a world' moment. The clarifications narrow the vision to something specific and achievable."
    },
    {
      id: 13,
      type: "capabilities-demo",
      section: "solution",
      title: "What If You Could...",
      capabilities: [
        { 
          text: "Test thousands of pricing scenarios instantly", 
          icon: Zap,
          example: "Run 10,000 price combinations in 30 seconds"
        },
        { 
          text: "Understand customer elasticity without harming real demand", 
          icon: Users,
          example: "See how price-sensitive each segment is"
        },
        { 
          text: "See margin, volume, and risk together — not in silos", 
          icon: BarChart3,
          example: "One dashboard, one truth"
        },
        { 
          text: "Put guardrails around decisions instead of blind automation", 
          icon: Shield,
          example: "The AI advises, you decide"
        }
      ],
      speakerNotes: "Each capability answers one of the earlier questions. Your audience should be connecting the dots. The examples make it tangible."
    },
    {
      id: 14,
      type: "philosophy-visual",
      section: "solution",
      title: "A Different Relationship With AI",
      insight: "The most powerful AI doesn't replace decision-makers. It makes them better.",
      comparison: {
        old: {
          label: "OLD MODEL",
          text: "AI sets the price",
          result: "Leaders feel replaced"
        },
        new: {
          label: "NEW MODEL",
          points: [
            "AI explains the economic landscape",
            "Humans choose the strategy",
            "The system protects from unintended consequences"
          ],
          result: "Leaders feel empowered"
        }
      },
      speakerNotes: "This is your philosophy statement. AI as co-pilot, not autopilot. This resonates with leaders who feel threatened by AI narratives."
    },
    {
      id: 15,
      type: "broader-impact",
      section: "impact",
      title: "This Isn't Just About Prices",
      subtitle: "It's about how organisations adopt AI responsibly.",
      contrast: {
        losers: [
          "Companies that automate the most",
          "Companies that deploy the flashiest models"
        ],
        winners: [
          "Companies that embed AI into decision workflows",
          "Companies that keep humans in control",
          "Companies that make trade-offs explicit"
        ]
      },
      speakerNotes: "Zoom out. Connect pricing to the bigger AI conversation. Your audience is thinking about AI across their business. Show them pricing as a template for responsible AI adoption."
    },
    {
      id: 16,
      type: "impact-visual",
      section: "impact",
      title: "Pricing Touches Everything",
      areas: [
        { label: "Revenue", icon: DollarSign, color: "text-green-400" },
        { label: "Customers", icon: Users, color: "text-blue-400" },
        { label: "Trust", icon: Shield, color: "text-purple-400" },
        { label: "Leadership Accountability", icon: Target, color: "text-yellow-400" }
      ],
      insight: "When you fix pricing, you often fix how decisions are made everywhere else.",
      speakerNotes: "Pricing is the canary in the coal mine for decision quality. If you can make pricing decisions well, you can make any decision well."
    },
    {
      id: 17,
      type: "closing-build",
      section: "closing",
      title: "The Real Risk",
      build: [
        "If pricing is your strongest profit lever —",
        "and every price is a prediction —",
        "then the real risk isn't using AI.",
        "The real risk is continuing to guess."
      ],
      speakerNotes: "Build this slowly. Each line should land. The final line is your thesis restated as a warning. Let it sit in silence for a moment."
    },
    {
      id: 18,
      type: "final",
      section: "closing",
      message: "The future of AI in business isn't about machines making decisions for us.",
      emphasis: "It's about giving leaders the clarity to make better ones themselves.",
      closing: "Thank you.",
      speakerNotes: "Deliver the message with conviction. The emphasis line is your final gift to the audience. 'Thank you' should be warm and genuine. Hold for applause."
    }
  ];

  const getSectionColor = (section) => sectionColors[section] || sectionColors.opening;

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide(currentSlide + 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide(currentSlide - 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowRight' || e.key === ' ') {
      nextSlide();
    } else if (e.key === 'ArrowLeft') {
      prevSlide();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide]);

  const handlePrint = () => {
    window.print();
  };

  const renderSlide = (slide) => {
    const bgGradient = getSectionColor(slide.section);
    
    switch (slide.type) {
      case "title":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 bg-gradient-to-br ${bgGradient}`}>
            <div className="mb-6 animate-pulse">
              <span className="text-red-500 font-bold text-2xl tracking-widest">TED</span>
            </div>
            <div className="text-6xl mb-6">{slide.visual}</div>
            <h1 className="text-3xl md:text-5xl font-bold text-[#F8FAFC] mb-4 leading-tight animate-fade-in">
              {slide.title}
            </h1>
            <p className="text-xl md:text-3xl text-[#2563EB] font-medium mb-8">
              {slide.subtitle}
            </p>
            <p className="text-lg text-[#94A3B8] mb-2">{slide.speaker}</p>
            <p className="text-sm text-[#64748B] max-w-lg">{slide.note}</p>
          </div>
        );

      case "audience":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-[#2563EB] font-semibold mb-4 uppercase tracking-wider">Opening</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-8">{slide.title}</h2>
            <div className="space-y-6 mb-8">
              {slide.prompts.map((prompt, idx) => (
                <div key={idx} className="bg-[#111827]/80 border border-yellow-500/30 rounded-xl p-6">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
                      <Hand className="w-6 h-6 text-yellow-400" />
                    </div>
                    <p className="text-xl text-[#F8FAFC] font-medium">{prompt.text}</p>
                  </div>
                  <p className="text-sm text-yellow-400 ml-16 italic">🎤 {prompt.pause}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#2563EB]/10 border-l-4 border-[#2563EB] p-4 rounded-r-xl">
              <p className="text-lg text-[#F8FAFC]">{slide.observation}</p>
            </div>
          </div>
        );

      case "stat-animated":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-[#2563EB] font-semibold mb-6 uppercase tracking-wider">The Power of Pricing</p>
            <div className="flex items-center gap-6 md:gap-12 flex-wrap justify-center mb-8">
              <div className="text-center">
                <AnimatedCounter 
                  end={slide.stats[0].value} 
                  suffix={slide.stats[0].suffix}
                  className={`text-6xl md:text-8xl font-bold ${slide.stats[0].color}`}
                />
                <p className="text-lg text-[#94A3B8] mt-2">{slide.stats[0].label}</p>
              </div>
              <div className="text-2xl text-[#64748B] max-w-[200px] text-center">{slide.message}</div>
              <div className="text-center">
                <AnimatedCounter 
                  end={slide.stats[1].value} 
                  suffix={slide.stats[1].suffix}
                  className={`text-6xl md:text-8xl font-bold ${slide.stats[1].color}`}
                />
                <p className="text-lg text-[#94A3B8] mt-2">{slide.stats[1].label}</p>
              </div>
            </div>
            {slide.example && (
              <div className="bg-[#111827]/80 border border-[#2563EB]/30 rounded-xl p-5 max-w-2xl">
                <p className="text-sm text-[#2563EB] font-semibold mb-2">💡 REAL EXAMPLE</p>
                <p className="text-[#94A3B8] text-sm">{slide.example}</p>
              </div>
            )}
          </div>
        );

      case "visual-list":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-6">{slide.title}</h2>
            <div className="grid grid-cols-2 gap-4 mb-6">
              {slide.items.map((item, idx) => (
                <div key={idx} className="bg-[#111827]/80 border border-[#2563EB]/20 rounded-xl p-4 hover:border-[#2563EB]/50 transition-all group">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-3xl group-hover:scale-110 transition-transform">{item.icon}</span>
                    <span className="text-lg text-[#F8FAFC] font-semibold">{item.text}</span>
                  </div>
                  <p className="text-sm text-[#64748B] italic ml-12">{item.visual}</p>
                </div>
              ))}
            </div>
            <div className="bg-gradient-to-r from-red-500/10 to-transparent border-l-4 border-red-500 p-4 rounded-r-xl">
              <p className="text-lg text-[#F8FAFC]">{slide.contrast}</p>
            </div>
          </div>
        );

      case "problem-visual":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-red-400 font-semibold mb-4 uppercase tracking-wider">The Hidden Problem</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-6">{slide.title}</h2>
            <div className="flex flex-wrap gap-3 mb-6">
              {slide.tools.map((tool, idx) => {
                const Icon = tool.icon;
                return (
                  <div key={idx} className="bg-[#111827]/80 border border-[#2563EB]/30 rounded-lg px-4 py-3 flex items-center gap-2">
                    <Icon className="w-5 h-5 text-[#2563EB]" />
                    <span className="text-[#94A3B8]">{tool.name}</span>
                  </div>
                );
              })}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5">
                <p className="text-sm text-red-400 font-semibold mb-2">❌ THE MYTH</p>
                <p className="text-[#94A3B8]">{slide.truth.myth}</p>
              </div>
              <div className="bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-xl p-5">
                <p className="text-sm text-[#2563EB] font-semibold mb-2">✓ THE REALITY</p>
                <p className="text-[#F8FAFC] font-medium">{slide.truth.reality}</p>
              </div>
            </div>
          </div>
        );

      case "questions-reveal":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-red-400 font-semibold mb-4 uppercase tracking-wider">The Hidden Problem</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-6">{slide.title}</h2>
            <RevealList 
              items={slide.questions.map((q, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-[#111827]/60 rounded-lg p-4">
                  <span className="text-[#2563EB] text-2xl font-bold">?</span>
                  <p className="text-lg text-[#F8FAFC]">{q}</p>
                </div>
              ))}
            />
            <div className="mt-6 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
              <p className="text-yellow-400">{slide.conclusion}</p>
            </div>
          </div>
        );

      case "metaphor":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-red-400 font-semibold mb-4 uppercase tracking-wider">The Hidden Problem</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-6">{slide.title}</h2>
            <div className="text-center mb-6">
              <p className="text-xl text-[#94A3B8] mb-4">{slide.content.what}</p>
              <div className="flex justify-center gap-4 mb-6">
                {slide.content.with.map((item, idx) => (
                  <span key={idx} className="bg-red-500/20 text-red-400 px-4 py-2 rounded-full text-sm font-medium">
                    {item}
                  </span>
                ))}
              </div>
              <p className="text-3xl font-bold text-red-400">{slide.content.verdict}</p>
            </div>
            <div className="bg-[#111827]/80 border border-yellow-500/30 rounded-xl p-5">
              <p className="text-sm text-yellow-400 font-semibold mb-2">📖 REAL STORY: {slide.example.company}</p>
              <p className="text-[#94A3B8] mb-2">{slide.example.story}</p>
              <p className="text-[#F8FAFC] font-medium">{slide.example.lesson}</p>
            </div>
          </div>
        );

      case "section-bridge":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 bg-gradient-to-br ${bgGradient}`}>
            <div className="text-6xl mb-8">{slide.visual}</div>
            <h2 className="text-2xl md:text-3xl text-[#94A3B8] mb-8 max-w-2xl">
              {slide.title}
            </h2>
            <p className="text-5xl md:text-7xl font-bold text-[#F8FAFC] animate-pulse">
              {slide.answer}
            </p>
          </div>
        );

      case "traps-visual":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-12 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-cyan-400 font-semibold mb-3 uppercase tracking-wider">Why AI Hasn't Fixed Pricing</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-4">{slide.title}</h2>
            <div className="space-y-3">
              {slide.traps.map((trap, idx) => (
                <div key={idx} className="bg-[#111827]/80 border border-red-500/20 rounded-xl p-4 hover:border-red-500/50 transition-all">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <span className="text-3xl">{trap.visual}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-7 h-7 bg-red-500/20 rounded-full flex items-center justify-center text-red-400 font-bold text-sm">
                          {trap.number}
                        </span>
                        <p className="text-lg font-semibold text-[#F8FAFC]">{trap.title}</p>
                      </div>
                      <p className="text-[#94A3B8] text-sm mb-1">{trap.description}</p>
                      <p className="text-xs text-[#64748B] italic">💭 {trap.metaphor}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "consequence-chain":
        return (
          <div className={`flex flex-col items-center justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-8">{slide.title}</h2>
            <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
              {slide.chain.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <React.Fragment key={idx}>
                    <div className="bg-[#111827]/80 border border-[#2563EB]/30 rounded-xl p-4 text-center">
                      <Icon className="w-8 h-8 text-[#2563EB] mx-auto mb-2" />
                      <p className="text-sm text-[#F8FAFC]">{item.text}</p>
                    </div>
                    {idx < slide.chain.length - 1 && (
                      <ArrowRight className="w-6 h-6 text-[#64748B]" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <p className="text-lg text-[#94A3B8] text-center max-w-2xl">{slide.conclusion}</p>
          </div>
        );

      case "reframe-visual":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-purple-400 font-semibold mb-6 uppercase tracking-wider">The Reframe</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-10">{slide.title}</h2>
            <div className="flex items-center gap-6 md:gap-12 flex-wrap justify-center">
              <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 md:p-8 text-center transform hover:scale-105 transition-transform">
                <span className="text-4xl mb-3 block">{slide.before.visual}</span>
                <p className="text-xs text-red-400 mb-2 uppercase tracking-wider">{slide.before.label}</p>
                <p className="text-lg md:text-xl text-red-400 line-through">{slide.before.text}</p>
              </div>
              <div className="text-4xl animate-pulse">→</div>
              <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-6 md:p-8 text-center transform hover:scale-105 transition-transform">
                <span className="text-4xl mb-3 block">{slide.after.visual}</span>
                <p className="text-xs text-green-400 mb-2 uppercase tracking-wider">{slide.after.label}</p>
                <p className="text-lg md:text-xl text-green-400 font-semibold">{slide.after.text}</p>
              </div>
            </div>
          </div>
        );

      case "needs-reveal":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-purple-400 font-semibold mb-4 uppercase tracking-wider">The Reframe</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-2">{slide.title}</h2>
            <p className="text-lg text-[#94A3B8] mb-6">{slide.subtitle}</p>
            <RevealList 
              items={slide.needs.map((need, idx) => {
                const Icon = need.icon;
                return (
                  <div key={idx} className="flex items-center gap-4 bg-[#111827]/60 rounded-lg p-4">
                    <div className="w-10 h-10 bg-[#2563EB]/20 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#2563EB]" />
                    </div>
                    <p className="text-lg text-[#F8FAFC]">{need.text}</p>
                  </div>
                );
              })}
            />
            <div className="mt-6 bg-[#2563EB]/10 border-l-4 border-[#2563EB] p-4 rounded-r-xl">
              <p className="text-[#2563EB] font-medium">{slide.conclusion}</p>
            </div>
          </div>
        );

      case "vision-visual":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-green-400 font-semibold mb-6 uppercase tracking-wider">The AxioSera Idea</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-6">{slide.title}</h2>
            <p className="text-xl md:text-2xl text-[#2563EB] mb-8 max-w-3xl font-medium">
              "{slide.question}"
            </p>
            <div className="flex gap-4 flex-wrap justify-center">
              {slide.clarifications.map((c, idx) => (
                <div key={idx} className="bg-[#111827]/80 border border-[#2563EB]/30 rounded-xl px-5 py-3 flex items-center gap-2">
                  <span className="text-xl">{c.visual}</span>
                  <span className="text-[#94A3B8]">{c.text}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case "capabilities-demo":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-12 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-green-400 font-semibold mb-3 uppercase tracking-wider">The AxioSera Idea</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-4">{slide.title}</h2>
            <div className="grid md:grid-cols-2 gap-3">
              {slide.capabilities.map((cap, idx) => {
                const CapIcon = cap.icon;
                return (
                  <div key={idx} className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-green-500/30 rounded-xl p-4 hover:border-green-500/60 transition-all group">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                        <CapIcon className="w-5 h-5 text-green-400" />
                      </div>
                      <p className="text-[#F8FAFC] font-medium">{cap.text}</p>
                    </div>
                    <p className="text-xs text-[#64748B] ml-13 italic">→ {cap.example}</p>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "philosophy-visual":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-green-400 font-semibold mb-4 uppercase tracking-wider">The AxioSera Idea</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-4">{slide.title}</h2>
            <div className="bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-xl p-5 mb-6">
              <p className="text-lg md:text-xl text-[#2563EB] font-medium text-center">{slide.insight}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-5">
                <p className="text-sm text-red-400 font-semibold mb-3">{slide.comparison.old.label}</p>
                <p className="text-lg text-red-400 line-through mb-2">{slide.comparison.old.text}</p>
                <p className="text-sm text-[#64748B]">→ {slide.comparison.old.result}</p>
              </div>
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-5">
                <p className="text-sm text-green-400 font-semibold mb-3">{slide.comparison.new.label}</p>
                {slide.comparison.new.points.map((item, idx) => (
                  <p key={idx} className="text-[#F8FAFC] mb-1 flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                    {item}
                  </p>
                ))}
                <p className="text-sm text-green-400 mt-2">→ {slide.comparison.new.result}</p>
              </div>
            </div>
          </div>
        );

      case "broader-impact":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-purple-400 font-semibold mb-4 uppercase tracking-wider">Why This Matters Beyond Pricing</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-2">{slide.title}</h2>
            <p className="text-lg text-[#94A3B8] mb-6">{slide.subtitle}</p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-red-400 font-semibold mb-3">❌ THE NEXT DECADE WON'T BE WON BY:</p>
                {slide.contrast.losers.map((item, idx) => (
                  <p key={idx} className="text-[#64748B] mb-2 line-through opacity-60">{item}</p>
                ))}
              </div>
              <div>
                <p className="text-sm text-green-400 font-semibold mb-3">✓ IT WILL BE WON BY:</p>
                {slide.contrast.winners.map((item, idx) => (
                  <p key={idx} className="text-[#F8FAFC] mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                    {item}
                  </p>
                ))}
              </div>
            </div>
          </div>
        );

      case "impact-visual":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-purple-400 font-semibold mb-4 uppercase tracking-wider">Why This Matters Beyond Pricing</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-6">{slide.title}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {slide.areas.map((area, idx) => {
                const AreaIcon = area.icon;
                return (
                  <div key={idx} className="bg-[#111827]/80 border border-[#2563EB]/20 rounded-xl p-5 text-center hover:scale-105 transition-transform">
                    <AreaIcon className={`w-10 h-10 ${area.color} mx-auto mb-2`} />
                    <p className="text-[#F8FAFC] font-medium">{area.label}</p>
                  </div>
                );
              })}
            </div>
            <div className="bg-[#2563EB]/10 border-l-4 border-[#2563EB] p-5 rounded-r-xl">
              <p className="text-lg text-[#F8FAFC] font-medium">{slide.insight}</p>
            </div>
          </div>
        );

      case "closing-build":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-[#2563EB] font-semibold mb-8 uppercase tracking-wider">Closing</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-8">{slide.title}</h2>
            <div className="space-y-4 max-w-2xl">
              {slide.build.map((line, idx) => (
                <p 
                  key={idx} 
                  className={`text-xl md:text-2xl ${
                    idx === slide.build.length - 1 
                      ? 'text-red-400 font-bold text-2xl md:text-3xl mt-4' 
                      : 'text-[#94A3B8]'
                  }`}
                >
                  {line}
                </p>
              ))}
            </div>
          </div>
        );

      case "final":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 bg-gradient-to-br ${bgGradient}`}>
            <Sparkles className="w-16 h-16 text-[#2563EB] mb-8 animate-pulse" />
            <p className="text-xl md:text-2xl text-[#94A3B8] mb-6 max-w-3xl">{slide.message}</p>
            <p className="text-2xl md:text-4xl font-bold text-[#F8FAFC] mb-12 max-w-3xl">
              {slide.emphasis}
            </p>
            <p className="text-3xl text-[#2563EB] font-medium">{slide.closing}</p>
          </div>
        );

      default:
        return null;
    }
  };

  const currentSlideData = slides[currentSlide];

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <style>
        {`
          @media print {
            body { background: white !important; }
            .no-print { display: none !important; }
          }
          @keyframes fade-in {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in {
            animation: fade-in 0.5s ease-out;
          }
        `}
      </style>

      <section className="pt-32 pb-8 px-6 no-print">
        <div className="max-w-6xl mx-auto">
          {/* Controls */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Presentation className="w-5 h-5 text-[#2563EB]" />
                <span className="text-[#94A3B8]">TED Talk</span>
              </div>
              <span className="text-sm px-3 py-1 bg-[#2563EB]/10 text-[#2563EB] rounded-full capitalize">
                {currentSlideData.section}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[#94A3B8] text-sm">
                {currentSlide + 1} / {slides.length}
              </span>
              <Button 
                onClick={() => setShowNotes(!showNotes)}
                variant="outline" 
                size="sm"
                className={`border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10 ${showNotes ? 'bg-[#2563EB]/20' : ''}`}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Notes
              </Button>
              <Button 
                onClick={handlePrint}
                variant="outline" 
                size="sm"
                className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </div>

          {/* Slide Display */}
          <div className={`bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-2xl overflow-hidden shadow-2xl shadow-[#2563EB]/10 transition-opacity duration-300 ${isAnimating ? 'opacity-50' : 'opacity-100'}`}>
            <div className="aspect-video relative">
              {renderSlide(currentSlideData)}
            </div>
          </div>

          {/* Speaker Notes */}
          {showNotes && currentSlideData.speakerNotes && (
            <div className="mt-4 bg-[#111827] border border-yellow-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4 text-yellow-400" />
                <span className="text-sm font-semibold text-yellow-400">SPEAKER NOTES</span>
              </div>
              <p className="text-[#94A3B8] text-sm">{currentSlideData.speakerNotes}</p>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <Button
              onClick={prevSlide}
              disabled={currentSlide === 0}
              variant="outline"
              className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10 disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Previous
            </Button>
            
            <div className="flex-1 mx-8">
              <div className="h-2 bg-[#111827] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#2563EB] to-[#3B82F6] transition-all duration-300"
                  style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                />
              </div>
              <div className="flex justify-between mt-2">
                {['Opening', 'Problem', 'AI Traps', 'Reframe', 'Solution', 'Impact', 'Close'].map((label, idx) => (
                  <span key={idx} className="text-xs text-[#64748B]">{label}</span>
                ))}
              </div>
            </div>

            <Button
              onClick={nextSlide}
              disabled={currentSlide === slides.length - 1}
              className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white disabled:opacity-30"
            >
              Next
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>

          {/* Keyboard hint */}
          <p className="text-center text-[#64748B] text-xs mt-4">
            Use ← → arrow keys or spacebar to navigate
          </p>

          {/* Slide Thumbnails */}
          <div className="mt-6 overflow-x-auto pb-4">
            <div className="flex gap-2 min-w-max">
              {slides.map((slide, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`w-20 h-12 rounded-lg border-2 transition-all flex flex-col items-center justify-center text-xs ${
                    currentSlide === idx 
                      ? 'border-[#2563EB] bg-[#2563EB]/20' 
                      : 'border-[#2563EB]/20 bg-[#111827] hover:border-[#2563EB]/50'
                  }`}
                >
                  <span className="text-[#94A3B8] font-medium">{idx + 1}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-6 no-print">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-[#2563EB]/20 to-[#111827] border border-[#2563EB]/40 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-[#F8FAFC] mb-4">Ready to Stop Guessing?</h2>
            <p className="text-[#94A3B8] mb-6">
              See where your pricing risk hides—before it costs you.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link to="/pricing-check">
                <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6">
                  Run Free Pricing Check
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/strategy">
                <Button variant="outline" className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10 px-8 py-6">
                  View SMB Playbook
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TEDTalkPage;
